import { useCallback, useState } from 'react';
import Papa from 'papaparse';
import { Download, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';

// May 14 batch — CSV upload entry for Add Property Step 2. Parses
// client-side (papaparse), validates header + caps, and hands back a
// flat row list to the parent. The parent merges these into the same
// BuildingDraft list used by the manual entry path and routes the
// submit through POST /api/buildings/bulk (no backend awareness of CSV).
//
// Optional `sn` column auto-assigns a provisioned-but-unassigned
// device from the org inventory to the new unit at submit time. Blank
// = no device. Server validates serials and surfaces row errors for
// not-found / cross-org / already-assigned / duplicate-in-submit.
//
// May 15 batch: template expanded with `device_type`, `multiplier`,
// `meter_sn`, `imr` (the last three are SALT-only and the backend
// errors on a row that fills them without device_type=SALT).

export type CsvDeviceType = 'WaterMeter' | 'SALTTransmitter';

export interface CsvRow {
  building: string;
  unit: string;
  deviceSerial?: string;
  deviceType?: CsvDeviceType;
  multiplier?: string;
  meterSerial?: string;
  imr?: number;
}

interface ParseResult {
  rows: CsvRow[];
  warnings: string[];
}

const MAX_ROWS = 5000;
const WARN_ROWS = 1000;

interface Props {
  onParsed: (result: ParseResult) => void;
  onError: (message: string) => void;
}

// Each column gets a hint cell directly under its header so Excel/Sheets
// renders the guidance in the right column instead of dumping prose into
// column A. The hint row starts with `#`, which papaparse strips
// (`comments: '#'`) so the row never reaches the bulk-import endpoint.
// Hints must stay comma-free per cell — quoting works but extra rules
// for users to remember are worse than terse phrasing.
const TEMPLATE_CSV = `building,unit,device_type,sn,multiplier,meter_sn,imr
# REQUIRED,REQUIRED,WaterMeter or SALTTransmitter,Provisioned SN from inventory,SALT only: e.g. 1:10 or integer N (=1:N),SALT only: connected meter SN,SALT only: integer ≥ 0
Trailer A,1,WaterMeter,SN-1001,,,
Trailer A,2,SALTTransmitter,SN-1002,1:10,MTR-9001,12345
Trailer A,3,,,,,
Trailer B,Suite 101,SALTTransmitter,SN-2050,1:100,MTR-9050,0
`;

function parseDeviceType(raw: string): CsvDeviceType | undefined {
  const v = raw.trim().toLowerCase();
  if (!v) return undefined;
  if (v === 'water' || v === 'watermeter' || v === 'water meter' || v === 'water_meter') return 'WaterMeter';
  if (v === 'salt' || v === 'salttransmitter' || v === 'salt transmitter' || v === 'salt_transmitter') return 'SALTTransmitter';
  return undefined;
}

function downloadTemplate() {
  const blob = new Blob([TEMPLATE_CSV], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'property-template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

export function CsvUploadStep({ onParsed, onError }: Props) {
  const [parsing, setParsing] = useState(false);

  const handleFile = useCallback((file: File) => {
    setParsing(true);
    Papa.parse<Record<string, string>>(file, {
      header: true,
      skipEmptyLines: 'greedy',
      comments: '#',
      transformHeader: (h) => h.trim().toLowerCase(),
      complete: (results) => {
        setParsing(false);
        if (results.errors.length > 0) {
          onError(`CSV parse error: ${results.errors[0].message}`);
          return;
        }
        const data = results.data;
        if (data.length === 0) {
          onError('CSV is empty.');
          return;
        }
        if (data.length > MAX_ROWS) {
          onError(`CSV has ${data.length} rows; cap is ${MAX_ROWS}.`);
          return;
        }
        // Header validation — building + unit required. Other columns
        // are optional. Accept a few alias spellings (device/sn, device_type/devicetype,
        // meter_sn/metersn, etc.) so handcrafted CSVs don't bounce.
        const firstRow = data[0];
        if (!('building' in firstRow) || !('unit' in firstRow)) {
          onError('CSV must include header row with `building` and `unit` columns.');
          return;
        }
        const rows: CsvRow[] = [];
        for (const r of data) {
          const building = (r.building ?? '').trim();
          const unit = (r.unit ?? '').trim();
          const serial = (r.sn ?? r.device ?? r.deviceserial ?? r['device serial'] ?? '').trim();
          const rawType = (r.device_type ?? r.devicetype ?? r['device type'] ?? '').trim();
          const multiplier = (r.multiplier ?? '').trim();
          const meterSerial = (r.meter_sn ?? r.metersn ?? r['meter sn'] ?? r.meterserial ?? '').trim();
          const imrRaw = (r.imr ?? '').trim();
          if (!building || !unit) continue; // skip blank rows
          const imr = imrRaw === '' ? undefined : Number(imrRaw);
          rows.push({
            building,
            unit,
            deviceSerial: serial || undefined,
            deviceType: parseDeviceType(rawType),
            multiplier: multiplier || undefined,
            meterSerial: meterSerial || undefined,
            imr: Number.isFinite(imr) ? (imr as number) : undefined,
          });
        }
        if (rows.length === 0) {
          onError('CSV has no usable rows after stripping blanks.');
          return;
        }
        const warnings: string[] = [];
        if (rows.length > WARN_ROWS) {
          warnings.push(`Large upload (${rows.length} rows) — validation may take a few seconds.`);
        }
        onParsed({ rows, warnings });
      },
      error: (err: Error) => {
        setParsing(false);
        onError(`Failed to parse CSV: ${err.message}`);
      },
    });
  }, [onParsed, onError]);

  return (
    <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-4 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Upload CSV</p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Download the template — the row directly under the headers shows each column's format (it's a comment and gets stripped on upload). Required columns are <code className="font-mono text-[11px]">building</code> and <code className="font-mono text-[11px]">unit</code>; device columns only apply when <code className="font-mono text-[11px]">sn</code> is filled. Up to {MAX_ROWS} rows.
          </p>
        </div>
        <Button type="button" variant="ghost" size="sm" onClick={downloadTemplate}>
          <Download className="w-3.5 h-3.5" />
          Template
        </Button>
      </div>
      <label className="block">
        <input
          type="file"
          accept=".csv,text/csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFile(file);
            e.target.value = ''; // allow re-uploading the same file
          }}
        />
        <span className="inline-flex items-center gap-2 px-3 py-1.5 text-sm rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer">
          <Upload className="w-3.5 h-3.5" />
          {parsing ? 'Parsing…' : 'Choose CSV file'}
        </span>
      </label>
    </div>
  );
}
